import { roundFCFA } from '@shared/utils/round';
import type { TrancheIRPP } from '@shared/constants/ParametresPaieDates';
import { defaultParametresFiscauxPaie, type ParametresFiscauxPaie } from './ParametresFiscauxPaie';

/**
 * IRPP — FONCTION UNIQUE du dépôt (CGI art. 173-174).
 *
 * Méthode légale :
 *  1. base annuelle = base mensuelle × 12, la base mensuelle étant le brut moins les cotisations
 *     sociales salariales (art. 92) et moins la TCS (art. 351) — voir `baseIrppMensuelle` ;
 *  2. abattement frais professionnels art. 94 (20 %, plafond 10 M/an, sans plancher) si le
 *     paramètre daté `abattementFraisPro.actif` est vrai (défaut : vrai) ;
 *  3. revenu imposable arrondi au millier inférieur (art. 168), PUIS divisé par les parts ;
 *     Q = revenu imposable par part ;
 *  4. impôt par part K = max(0, t × Q − N) ;
 *  5. IRPP annuel = K × parts ; IRPP mensuel = annuel / 12 arrondi à l'unité (ROUND).
 *
 * Barème, abattement et exonération lus dans les paramètres datés : aucun taux en dur ici.
 */

/**
 * Base IRPP mensuelle légale : base TCS (brut − CNSS − CNAMGS salariales, art. 92) moins la TCS
 * elle-même, déductible (art. 351). La base TCS exclut déjà les primes exonérées de l'art. 91 11° (annuel et global,
 * IRPP et TCS) : `exoArt9111` ne sert qu'à un appelant dont la base TCS ne les a pas encore retirées.
 */
export function baseIrppMensuelle(baseTcs: number, tcs: number, exoArt9111 = 0): number {
  return Math.max(0, baseTcs - tcs - exoArt9111);
}

export interface CalculIRPPOptions {
  /** Surcharge ponctuelle de `params.abattementFraisPro.actif`. */
  appliquerAbattementFraisPro?: boolean;
  params?: ParametresFiscauxPaie;
}

export interface CalculIRPPResult {
  /** Abattement frais professionnels, ramené au mois. */
  abattement: number;
  /** Abattement frais professionnels annuel exact (art. 94). */
  abattement_annuel: number;
  /** Base imposable mensuelle après abattement. */
  net_imposable: number;
  /** Base imposable annuelle après abattement. */
  net_imposable_annuel: number;
  parts: number;
  /** Q : revenu annuel imposable par part (total arrondi au millier inférieur, divisé par les parts). */
  quotient_familial: number;
  /** Taux t de la tranche de Q. */
  taux: number;
  /** Déduction N de la tranche de Q. */
  deduction: number;
  /** K : impôt annuel pour une part. */
  impot_par_part: number;
  irpp_annuel: number;
  irpp_mensuel: number;
}

/** Tranche applicable à Q (borne haute incluse). */
export function trancheIrppPour(q: number, bareme: readonly TrancheIRPP[]): TrancheIRPP {
  for (const t of bareme) {
    if (t.max === null || q <= t.max) return t;
  }
  return bareme[bareme.length - 1]!;
}

/** Impôt annuel pour UNE part : max(0, t × Q − N). Q est supposé déjà arrondi au millier. */
export function calculerIRPPParPart(
  q: number,
  params: ParametresFiscauxPaie = defaultParametresFiscauxPaie(),
): number {
  if (q <= 0) return 0;
  const t = trancheIrppPour(q, params.baremeIrpp);
  return Math.max(0, t.taux * q - t.deduction);
}

/** Abattement annuel art. 94 : min(taux × base, plafond) ; 0 si désactivé. */
export function calculAbattementIRPP(
  baseAnnuelle: number,
  params: ParametresFiscauxPaie = defaultParametresFiscauxPaie(),
  actif: boolean = params.abattementFraisPro.actif,
): number {
  if (!actif) return 0;
  const base = Math.max(0, baseAnnuelle);
  const { taux, plafondAnnuel } = params.abattementFraisPro;
  return Math.min(roundFCFA(base * taux), plafondAnnuel);
}

/**
 * @param baseTcsMensuelle base IRPP du mois : brut soumis − CNSS − CNAMGS − TCS (voir
 *   `baseIrppMensuelle`), avant abattement art. 94.
 * @param parts nombre de parts fiscales
 */
export function calculerIRPP(
  baseTcsMensuelle: number,
  parts: number,
  options: CalculIRPPOptions = {},
): CalculIRPPResult {
  const params = options.params ?? defaultParametresFiscauxPaie();
  const actif = options.appliquerAbattementFraisPro ?? params.abattementFraisPro.actif;
  const baseAnnuelle = Math.max(0, baseTcsMensuelle) * 12;
  const abattementAnnuel = calculAbattementIRPP(baseAnnuelle, params, actif);
  const netAnnuel = Math.max(0, baseAnnuelle - abattementAnnuel);
  const partsEff = parts > 0 ? parts : 1;
  // Art. 168 : le revenu imposable total est arrondi au millier inférieur, puis divisé en parts.
  const q = (Math.floor(netAnnuel / 1000) * 1000) / partsEff;
  const tranche = trancheIrppPour(q, params.baremeIrpp);
  const impotParPart = q > 0 ? Math.max(0, tranche.taux * q - tranche.deduction) : 0;
  const irppAnnuel = impotParPart * partsEff;

  return {
    abattement: roundFCFA(abattementAnnuel / 12),
    abattement_annuel: roundFCFA(abattementAnnuel),
    net_imposable: roundFCFA(netAnnuel / 12),
    net_imposable_annuel: roundFCFA(netAnnuel),
    parts: partsEff,
    quotient_familial: q,
    taux: q > 0 ? tranche.taux : 0,
    deduction: q > 0 ? tranche.deduction : 0,
    impot_par_part: impotParPart,
    irpp_annuel: roundFCFA(irppAnnuel),
    irpp_mensuel: roundFCFA(irppAnnuel / 12),
  };
}
